import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Menu, ShoppingBag, MessageCircle } from "lucide-react";
import { SITE } from "../data/site";
import { useCart } from "../hooks/useCart";

const NAV = [
  { id: "collection", label: "Collection" },
  { id: "best-sellers", label: "Best Sellers" },
  { id: "reviews", label: "Reviews" },
  { id: "locations", label: "Location" },
  { id: "faq", label: "FAQ" },
];

const Header = ({ onOpenCart }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-[hsl(var(--cream))]/95 backdrop-blur-xl border-b border-[hsl(var(--border))]"
          : "bg-transparent"
      }`}
    >
      <div className="container-tjb flex items-center justify-between h-16 md:h-20">
        <a
          href="#top"
          data-testid="header-logo"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-2.5"
        >
          <img
            src={SITE.logoLight}
            alt={`${SITE.brand} logo`}
            className="h-8 md:h-9 w-auto"
          />
          <span className="hidden sm:inline font-display text-lg md:text-xl tracking-tight">
            {SITE.brand}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => go(n.id)}
              className="text-sm tracking-wide text-[hsl(var(--ink))]/80 hover:text-[hsl(var(--ink))] transition-colors"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a
            href={SITE.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp"
            className="hidden md:inline-flex btn-primary py-2.5 px-5"
          >
            <MessageCircle className="w-4 h-4" />
            Shop via WhatsApp
          </a>
          <button
            onClick={onOpenCart}
            data-testid="header-open-cart"
            className="relative p-2.5 rounded-full hover:bg-[hsl(var(--sand))] transition-colors"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span
                data-testid="header-cart-count"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--cream))] text-[10px] font-semibold flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="header-mobile-menu"
                className="lg:hidden p-2.5 rounded-full hover:bg-[hsl(var(--sand))]"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[hsl(var(--cream))] border-l border-[hsl(var(--border))] w-[88%] sm:w-[420px]"
            >
              <div className="flex flex-col h-full pt-6">
                <img
                  src={SITE.logoLight}
                  alt={SITE.brand}
                  className="h-10 w-auto mb-10"
                />
                <nav className="flex flex-col gap-5">
                  {NAV.map((n) => (
                    <button
                      key={n.id}
                      data-testid={`mobile-nav-${n.id}`}
                      onClick={() => go(n.id)}
                      className="text-left font-display text-2xl text-[hsl(var(--ink))]"
                    >
                      {n.label}
                    </button>
                  ))}
                </nav>
                <div className="mt-auto pb-6 flex flex-col gap-3">
                  <a
                    href={SITE.whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-whatsapp"
                    className="btn-primary justify-center"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Shop via WhatsApp
                  </a>
                  <p className="text-xs text-center text-[hsl(var(--muted-foreground))]">
                    {SITE.hours}
                  </p>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
